import type dayjs from 'dayjs'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type { LoanPurpose } from 'app/utils/constants'
import { MAX_LOAN_AMOUNT } from 'app/utils/constants'

// Separate state interfaces by domain
interface ProgressStepState {
    currentStep: number
    totalSteps: number
}

interface IdState {
    partnerSessionId: string | null
    campaignId: string | null
}

interface LoanState {
    loanAmount: number
    loanPurpose: LoanPurpose | string
    loanPurposeOther: string
    province: string
    languagePreference: string
    queryParams: string | null
}

interface PersonalState {
    firstName: string
    lastName: string
    email: string
    dateOfBirth: dayjs.Dayjs | null
    fullAddress: string
    address1: string
    address2: string
    province: string
    city: string
    postalCode: string
    phoneNumber: string
    residencyStatus: string
    residencyStatusOther: string
}

interface EmploymentState {
    employmentStatus: string
    occupationType: string
    jobTitle: string
    employerName: string
    jobStartDate: string
    annualIncome: number
    levelOfEducation: string
}

interface HousingState {
    housingStatus: string
    monthlyHousingPayment: number
    livedAtAddressSince: dayjs.Dayjs | null
}

interface FinancialState {
    /** '' until answered; financial-info step when flag-enabled */
    loanInLastThreeMonths: '' | 'YES' | 'NO'
    savingsCheckingAccounts: number
    savingsInvestmentAccounts: number
    vehicleOwnership: string
}

interface ResultState {
    lastCompletedStep: string
    status: string
    declineReason: string
    // TODO: Remove this once the type is defined
    prequalResponse: any
    applicationId: string
}

// Combine all state interfaces
interface State extends ProgressStepState, IdState, LoanState, PersonalState, EmploymentState, HousingState, FinancialState, ResultState {}

// Define action types for type safety
type Actions = {
    updateStep: (step: number) => void // Note: Implementation should include validation
    updatePartnerSessionId: (id: string) => void
    updateCampaignId: (id: string) => void
    updateLoanInfo: (data: Partial<LoanState>) => void
    updatePersonalInfo: (data: Partial<PersonalState>) => void
    updateEmploymentInfo: (data: Partial<EmploymentState>) => void
    updateFinancialInfo: (data: Partial<FinancialState>) => void
    updateHousingInfo: (data: Partial<HousingState>) => void
    updateResultInfo: (data: Partial<ResultState>) => void
    reset: () => void
}

// Combine state and actions
type PrequalStore = State & Actions

// Initial state
const initialState: State = {
    // Step Information
    currentStep: 1,
    totalSteps: 4,

    // ID Information
    partnerSessionId: null,
    campaignId: null,

    // Loan Information
    loanAmount: MAX_LOAN_AMOUNT,
    loanPurpose: '',
    loanPurposeOther: '',
    province: '',
    languagePreference: 'en',
    queryParams: null,
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: null,
    fullAddress: '',
    address1: '',
    address2: '',
    city: '',
    postalCode: '',
    phoneNumber: '',
    residencyStatus: '',
    residencyStatusOther: '',

    // Employment Information
    employmentStatus: '',
    occupationType: '',
    jobTitle: '',
    employerName: '',
    jobStartDate: '',
    annualIncome: 0,
    levelOfEducation: '',

    // Housing Information
    housingStatus: '',
    monthlyHousingPayment: 0,
    livedAtAddressSince: null,

    // Financial Information (affiliate + feature flag step)
    loanInLastThreeMonths: '',
    savingsCheckingAccounts: 0,
    savingsInvestmentAccounts: 0,
    vehicleOwnership: '',

    // Result Information
    lastCompletedStep: '',
    status: '',
    declineReason: '',
    prequalResponse: {},
    applicationId: ''
}

const usePrequalStore = create<PrequalStore>()(
    persist(
        (set) => ({
            ...initialState,

            // Action creators that update slices of state
            updateStep: (step) =>
                set((state) => {
                    // Ensure step is within valid range
                    const validStep = Math.min(Math.max(1, step), state.totalSteps)
                    return { ...state, currentStep: validStep }
                }),

            updatePartnerSessionId: (id) => set((state) => ({ ...state, partnerSessionId: id || null })),

            updateCampaignId: (id) => set((state) => ({ ...state, campaignId: id || null })),

            updateLoanInfo: (data) => set((state) => ({ ...state, ...data })),

            updatePersonalInfo: (data) => set((state) => ({ ...state, ...data })),

            updateEmploymentInfo: (data) => set((state) => ({ ...state, ...data })),

            updateFinancialInfo: (data) => set((state) => ({ ...state, ...data })),

            updateHousingInfo: (data) => set((state) => ({ ...state, ...data })),

            updateResultInfo: (data) => set((state) => ({ ...state, ...data })),

            // Reset the entire store to initial state
            reset: () => set(initialState)
        }),
        {
            name: 'prequal-storage',
            storage: createJSONStorage(() => localStorage)
        }
    )
)

/**
 * Hook for accessing and updating step information
 * @example
 * const { currentStep, totalSteps, updateStep } = useProgressStep();
 * updateStep(2); // Move to step 2
 */
export const useProgressStep = () => {
    const currentStep = usePrequalStore((state) => state.currentStep)
    const totalSteps = usePrequalStore((state) => state.totalSteps)
    const updateStep = usePrequalStore((state) => state.updateStep)

    return {
        currentStep,
        totalSteps,
        updateStep
    }
}

export const useIdInfo = () => {
    const partnerSessionId = usePrequalStore((state) => state.partnerSessionId)
    const campaignId = usePrequalStore((state) => state.campaignId)
    const updatePartnerSessionId = usePrequalStore((state) => state.updatePartnerSessionId)
    const updateCampaignId = usePrequalStore((state) => state.updateCampaignId)

    return {
        partnerSessionId,
        campaignId,
        updatePartnerSessionId,
        updateCampaignId
    }
}

/**
 * Hook for accessing and updating loan information
 * @example
 * const { loanAmount, updateLoanInfo } = useLoanInfo();
 * updateLoanInfo({ loanAmount: 25000, loanPurpose: 'Debt Consolidation' });
 */
export const useLoanInfo = () => {
    const loanAmount = usePrequalStore((state) => state.loanAmount)
    const loanPurpose = usePrequalStore((state) => state.loanPurpose)
    const loanPurposeOther = usePrequalStore((state) => state.loanPurposeOther)
    const province = usePrequalStore((state) => state.province)
    const updateLoanInfo = usePrequalStore((state) => state.updateLoanInfo)
    const languagePreference = usePrequalStore((state) => state.languagePreference)
    const isFromQC = usePrequalStore((state) => state.province === 'QC')
    const queryParams = usePrequalStore((state) => state.queryParams)

    return {
        loanAmount,
        loanPurpose,
        loanPurposeOther,
        province,
        updateLoanInfo,
        languagePreference,
        isFromQC,
        queryParams
    }
}

/**
 * Hook for accessing and updating personal information
 * @example
 * const { firstName, updatePersonalInfo } = usePersonalInfo();
 * updatePersonalInfo({ firstName: 'John', lastName: 'Doe', email: 'john@example.com' });
 */
export const usePersonalInfo = () => {
    const firstName = usePrequalStore((state) => state.firstName)
    const lastName = usePrequalStore((state) => state.lastName)
    const email = usePrequalStore((state) => state.email)
    const dateOfBirth = usePrequalStore((state) => state.dateOfBirth)
    const fullAddress = usePrequalStore((state) => state.fullAddress)
    const address1 = usePrequalStore((state) => state.address1)
    const address2 = usePrequalStore((state) => state.address2)
    const province = usePrequalStore((state) => state.province)
    const city = usePrequalStore((state) => state.city)
    const postalCode = usePrequalStore((state) => state.postalCode)
    const phoneNumber = usePrequalStore((state) => state.phoneNumber)
    const residencyStatus = usePrequalStore((state) => state.residencyStatus)
    const residencyStatusOther = usePrequalStore((state) => state.residencyStatusOther)
    const updatePersonalInfo = usePrequalStore((state) => state.updatePersonalInfo)

    return {
        firstName,
        lastName,
        email,
        dateOfBirth,
        fullAddress,
        address1,
        address2,
        province,
        city,
        postalCode,
        phoneNumber,
        residencyStatus,
        residencyStatusOther,
        updatePersonalInfo
    }
}

/**
 * Hook for accessing and updating employment information
 * @example
 * const { employmentStatus, updateEmploymentInfo } = useEmploymentInfo();
 * updateEmploymentInfo({ employmentStatus: 'Full-time', annualIncome: 75000 });
 */
export const useEmploymentInfo = () => {
    const employmentStatus = usePrequalStore((state) => state.employmentStatus)
    const occupationType = usePrequalStore((state) => state.occupationType)
    const jobTitle = usePrequalStore((state) => state.jobTitle)
    const employerName = usePrequalStore((state) => state.employerName)
    const jobStartDate = usePrequalStore((state) => state.jobStartDate)
    const annualIncome = usePrequalStore((state) => state.annualIncome)
    const levelOfEducation = usePrequalStore((state) => state.levelOfEducation)
    const updateEmploymentInfo = usePrequalStore((state) => state.updateEmploymentInfo)

    return {
        employmentStatus,
        occupationType,
        jobTitle,
        employerName,
        jobStartDate,
        annualIncome,
        levelOfEducation,
        updateEmploymentInfo
    }
}

export const useFinancialInfo = () => {
    const loanInLastThreeMonths = usePrequalStore((state) => state.loanInLastThreeMonths)
    const savingsCheckingAccounts = usePrequalStore((state) => state.savingsCheckingAccounts)
    const savingsInvestmentAccounts = usePrequalStore((state) => state.savingsInvestmentAccounts)
    const vehicleOwnership = usePrequalStore((state) => state.vehicleOwnership)
    const updateFinancialInfo = usePrequalStore((state) => state.updateFinancialInfo)

    return {
        loanInLastThreeMonths,
        savingsCheckingAccounts,
        savingsInvestmentAccounts,
        vehicleOwnership,
        updateFinancialInfo
    }
}

/**
 * Hook for accessing and updating housing information
 * @example
 * const { housingStatus, updateHousingInfo } = useHousingInfo();
 * updateHousingInfo({ housingStatus: 'Renting', monthlyHousingPayment: 2000 });
 */
export const useHousingInfo = () => {
    const housingStatus = usePrequalStore((state) => state.housingStatus)
    const monthlyHousingPayment = usePrequalStore((state) => state.monthlyHousingPayment)
    const livedAtAddressSince = usePrequalStore((state) => state.livedAtAddressSince)
    const updateHousingInfo = usePrequalStore((state) => state.updateHousingInfo)

    return {
        housingStatus,
        monthlyHousingPayment,
        livedAtAddressSince,
        updateHousingInfo
    }
}

/**
 * Hook for accessing and updating result information
 * @example
 * const { lastCompletedStep, status, declineReason, updateResultInfo } = useResultInfo();
 * updateResultInfo({ status: 'declined', declineReason: 'Credit Score Too Low' });
 */
export const useResultInfo = () => {
    const lastCompletedStep = usePrequalStore((state) => state.lastCompletedStep)
    const status = usePrequalStore((state) => state.status)
    const declineReason = usePrequalStore((state) => state.declineReason)
    const prequalResponse = usePrequalStore((state) => state.prequalResponse)
    const applicationId = usePrequalStore((state) => state.applicationId)
    const updateResultInfo = usePrequalStore((state) => state.updateResultInfo)

    return {
        lastCompletedStep,
        status,
        declineReason,
        prequalResponse,
        applicationId,
        updateResultInfo
    }
}

export default usePrequalStore
